<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedOwners"
      multi-select
      searchable
      :label="$t('Job owners')"
      :list="owners"
      :placeholder="$t('Select job owners to filter by')"
      max-labels-visible="5"
      can-deselect
    >
      <template #select-all-text>
        <WdsText :text="$t('Select all')" />
      </template>
    </WdsSelect>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import { computed, ComputedRef, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { ListItem } from '@/components/Workflows/Pages/Workflow/workflow'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { user } from '@/composables/useUser'

type OwnerFilter = ListItem & { id: string }

const selectedOwners: ModelRef<OwnerFilter[]> = defineModel({
  default: () => [],
})

const { selectedAccounts } = useAccountsStore()

const { result } = useQuery(
  graphql(`
    query JobOwners($accounts: [ID!]) {
      hires(accounts: $accounts, first: 100000) {
        data {
          job {
            owners {
              id
              value: id
              label: name
            }
          }
        }
      }
    }
  `),
  () => ({
    accounts: selectedAccounts.map((account) => account.id),
  }),
  {
    clientId: 'publicApiCustomFields',
  },
)

const owners: ComputedRef<OwnerFilter[]> = computed(() => {
  if (!result.value?.hires?.data) {
    return []
  }

  const allOwners: OwnerFilter[] = result.value.hires.data.flatMap((hire) => hire.job?.owners ?? [])

  const uniqueOwners: OwnerFilter[] = Array.from(
    new Map(allOwners.map((owner) => [owner.id, owner])).values(),
  )

  return uniqueOwners
    .map((owner) => (owner.id === user.value.globalId ? { ...owner, label: $t('My own') } : owner))
    .sort((a, b) => (a.id === user.value.globalId ? -1 : b.id === user.value.globalId ? 1 : 0))
})
</script>
